import { Dropdown, InputIcon } from '@platform/ui-library';
import {
  FormEvent,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Constant } from '@app/const';
import { Autocomplete, FocusGroup, ItemListFormProps } from '@app/form_components';
import { useAsync, useServices } from '@app/hooks';
import { RichTextReplyButton } from '@app/types';
import { createUuid, groupPropertiesByPrefix } from '@app/utilities';

const multilangPrefix = 'label.';

export type RichTextReplyButtonFormProps = ItemListFormProps<RichTextReplyButton>;

export function RichTextReplyButtonForm({
  onBlur,
  onChange,
  onDelete,
  onFocus,
  value
}: RichTextReplyButtonFormProps) {
  const { workflowService } = useServices();
  const [selectedType, setSelectedType] = useState(value?.urlType || 'value');
  const [url, setUrl] = useState(value?.url || '');
  const [intent, setIntent] = useState(value?.intent || '');
  const [languageOptions] = useAsync(() => workflowService.getLanguages(), []);

  const intents = window.apm.serialized.components.intents;
  const intentOptions = intents.reduce((acc: Record<string, string>, intent) => {
    acc[intent.id] = intent.name;
    return acc;
  }, {});

  const types: { value: string; name: string; }[] =
    window.apm.knowledgeBase.getNodeInputTypes();
  const typeOptions = types.reduce(
    (acc: Record<string, string>, { value, name }) => {
      acc[value] = name;
      return acc;
    },
    {}
  );

  const initialState: RichTextReplyButton = useMemo(() => {
    const languageCodes = window.apm.serialized.languages.map(
      ({ language }) => language
    );

    return {
      id: value?.id || '',
      title: value?.title || '',
      label: languageCodes.reduce(
        (acc, language) => ({
          ...acc,
          [language]: value?.label?.[language] || ''
        }),
        {}
      ),
      urlType: value?.urlType || Object.keys(typeOptions)[0],
      url: value?.url || '',
      intent: value?.intent || ''
    };
  }, [value]);

  useEffect(() => {
    setIntent(initialState.intent);
    setSelectedType(initialState.urlType);
  }, [initialState.intent, initialState.urlType]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const newValue = groupPropertiesByPrefix({
      ...Object.fromEntries(formData as any),
      id: value?.id ?? createUuid()
    }, multilangPrefix) as RichTextReplyButton;
    if (!newValue.intent) {
      return;
    }

    onChange(newValue);
  };

  return (
    <FocusGroup onBlur={onBlur} onFocus={onFocus}>
      <form onSubmit={handleSubmit}>
        <fieldset>
          <div className="flex-column">
            <label className="ui-label">Intent</label>
            <Dropdown
              options={intentOptions}
              placeholder="- Select an Intent -"
              onChange={setIntent}
              value={intent}
            />
            <input name="intent" required type="hidden" value={intent} />
          </div>
          {Object.keys(initialState.label).map((language, i) => (
            <InputIcon
              key={language}
              label={languageOptions?.find(({ value }) => value === language)?.label}
              name={`${multilangPrefix}${language}`}
              defaultValue={initialState.label[language]}
              required={i === 0}
            />
          ))}
          <div className="flex-column">
            <label className="ui-label">Type</label>
            <Dropdown
              options={typeOptions}
              onChange={setSelectedType}
              value={selectedType}
            />
            <input name="urlType" required type="hidden" value={selectedType} />
          </div>
          {selectedType === 'value' && (
            <InputIcon
              label="URL"
              placeholder="Enter URL (optional)"
              defaultValue={initialState.url}
              name="url"
            />
          )}
          {selectedType === 'variable' && (
            <Autocomplete<string>
              getOptions={getVariableOptions}
              label="URL"
              labels={{
                isLoading: 'Searching variables...',
                notFound: 'No variables found.'
              }}
              name="url"
              onChange={(val) => setUrl(val ?? '')}
              value={url}
              initSearchValue={url}
            />
          )}
        </fieldset>
        <div className='actions'>
          {value?.id && (
            <button
              type="button"
              onClick={onDelete}
              className="form-button normal warning"
              title="Remove"
            >
              <i className="fe icon-trash"></i>
            </button>
          )}
          <button type="submit" className="form-button normal">
            Save
          </button>
        </div>
      </form>
    </FocusGroup>
  );
}

function getVariableOptions(searchString: string) {
  const variables = window.apm.knowledgeBase.getVariableSuggestions(
    searchString,
    Constant.k.SCOPE_APPLICATION
  );
  if (!variables?.app?.length) {
    return [];
  }
  return variables.app.map((variable) => {
    const { varName } = variable.props.config;
    return {
      label: varName,
      value: `app.${varName}`
    };
  });
}
